import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { Building2, Mail, UserPlus } from "lucide-react";
import { backendClient } from "../../../../../../api/backend";

const PLAN_OPTIONS = [
  { value: "starter", label: "Starter" },
  { value: "pro", label: "Pro" },
  { value: "enterprise", label: "Enterprise" },
];

const OnboardOrgDialog = ({ open, onClose, org }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [plan, setPlan] = useState("starter");
  const [lang, setLang] = useState("fr");
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [existingUserInfo, setExistingUserInfo] = useState(null);
  const [result, setResult] = useState(null); // { created, invited, errors }

  const contacts = org?.contacts || [];

  useEffect(() => {
    if (!open) {
      setPlan("starter");
      setLang("fr");
      setSelectedContactIds(new Set());
      setError("");
      setExistingUserInfo(null);
      setResult(null);
    }
  }, [open]);

  const toggleContact = (contactId) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
    setError("");
    setExistingUserInfo(null);
  };

  const toggleAll = () => {
    if (selectedContactIds.size === contacts.length) {
      setSelectedContactIds(new Set());
    } else {
      setSelectedContactIds(new Set(contacts.map((c) => c.id)));
    }
  };

  const selectedContacts = contacts.filter((c) => selectedContactIds.has(c.id));

  const handleSubmit = async () => {
    if (selectedContacts.length === 0) {
      setError("Sélectionnez au moins un contact à inviter.");
      return;
    }

    const ownerContact = selectedContacts[0];
    if (!ownerContact.email) {
      setError("Le premier contact sélectionné n'a pas d'adresse email.");
      return;
    }

    setLoading(true);
    setError("");
    setExistingUserInfo(null);

    const inviteResults = { created: false, invited: 0, errors: [] };

    try {
      // Step 1: Create organization + invite owner via inviteOrg
      const orgResponse = await backendClient.inviteOrg({
        email: ownerContact.email,
        plan,
        billing_cycle: "monthly",
        lang,
        organization_name: org?.name || undefined,
      });

      inviteResults.created = true;
      inviteResults.invited = 1;

      const newOrgId = orgResponse?.id;

      // Step 2: Invite remaining contacts
      if (newOrgId && selectedContacts.length > 1) {
        const remaining = selectedContacts.slice(1);
        for (const contact of remaining) {
          if (!contact.email) {
            inviteResults.errors.push(`${contact.first_name || "Contact"}: pas d'email`);
            continue;
          }
          try {
            await backendClient.inviteAdminUser({
              email: contact.email,
              organization_id: newOrgId,
              roles: ["user"],
              lang,
            });
            inviteResults.invited++;
          } catch (err) {
            inviteResults.errors.push(
              `${contact.email}: ${err.message || "Erreur"}`
            );
          }
        }
      }

      setResult(inviteResults);
    } catch (err) {
      if (err.existingUser) {
        setExistingUserInfo(err.existingUser);
      } else {
        setError(err.message || "Erreur lors de la création de l'organisation");
      }
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark
            ? "0 4px 20px rgba(0,0,0,0.5)"
            : "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 3,
          px: 3,
          fontWeight: 600,
          fontSize: "1.25rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Building2 size={18} color={theme.palette.primary.main} />
        </Box>
        Créer Organisation & Inviter
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* Success state */}
        {result ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }}>
              <AlertTitle sx={{ fontWeight: 600 }}>Organisation créée</AlertTitle>
              <Typography variant="body2">
                Organisation créée avec succès.{" "}
                {result.invited} invitation{result.invited > 1 ? "s" : ""} envoyée{result.invited > 1 ? "s" : ""}.
              </Typography>
            </Alert>
            {result.errors.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: "8px" }}>
                <AlertTitle sx={{ fontWeight: 600 }}>
                  Certaines invitations ont échoué
                </AlertTitle>
                {result.errors.map((err, i) => (
                  <Typography key={i} variant="body2">
                    {err}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2.5, color: theme.palette.text.secondary }}>
              Créer une organisation Lawis à partir de{" "}
              <strong>{org?.name}</strong> et inviter les contacts sélectionnés.
              Le premier contact sélectionné sera le propriétaire de l'organisation.
            </DialogContentText>

            {/* Existing user warning */}
            {existingUserInfo && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: "8px" }}>
                <AlertTitle sx={{ fontWeight: 600 }}>
                  Utilisateur déjà existant
                </AlertTitle>
                <Typography variant="body2">
                  <strong>Email:</strong> {existingUserInfo.email}
                </Typography>
                {existingUserInfo.organizationName && (
                  <Typography variant="body2">
                    <strong>Organisation:</strong>{" "}
                    {existingUserInfo.organizationName}
                  </Typography>
                )}
                {existingUserInfo.subscriptionPlan && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Plan:</strong> {existingUserInfo.subscriptionPlan}
                    </Typography>
                    {existingUserInfo.subscriptionStatus && (
                      <Chip
                        label={existingUserInfo.subscriptionStatus}
                        size="small"
                        color={
                          existingUserInfo.subscriptionStatus === "active"
                            ? "success"
                            : existingUserInfo.subscriptionStatus === "trial"
                            ? "info"
                            : "default"
                        }
                      />
                    )}
                  </Box>
                )}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                {error}
              </Alert>
            )}

            {/* Plan + Language */}
            <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={plan}
                  label="Plan"
                  onChange={(e) => setPlan(e.target.value)}
                  sx={{ borderRadius: "8px" }}
                >
                  {PLAN_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Langue email</InputLabel>
                <Select
                  value={lang}
                  label="Langue email"
                  onChange={(e) => setLang(e.target.value)}
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Contacts selection */}
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <UserPlus size={14} color={theme.palette.text.secondary} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Contacts à inviter ({selectedContactIds.size}/{contacts.length})
                  </Typography>
                </Box>
                {contacts.length > 0 && (
                  <Button
                    size="small"
                    onClick={toggleAll}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    {selectedContactIds.size === contacts.length
                      ? "Tout désélectionner"
                      : "Tout sélectionner"}
                  </Button>
                )}
              </Box>

              {/* Contact list */}
              <Box sx={{ maxHeight: 260, overflow: "auto" }}>
                {contacts.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3, px: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.disabled }}
                    >
                      Aucun contact lié à cette organisation.
                      <br />
                      Liez des contacts d'abord.
                    </Typography>
                  </Box>
                ) : (
                  contacts.map((contact, idx) => {
                    const isSelected = selectedContactIds.has(contact.id);
                    const isOwner = isSelected && selectedContacts[0]?.id === contact.id;
                    return (
                      <Box
                        key={contact.id}
                        onClick={() => toggleContact(contact.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 2,
                          py: 1,
                          cursor: "pointer",
                          borderBottom:
                            idx < contacts.length - 1
                              ? `1px solid ${theme.palette.divider}`
                              : "none",
                          bgcolor: isSelected
                            ? alpha(theme.palette.primary.main, 0.04)
                            : "transparent",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                          },
                          transition: "background 0.15s",
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{ p: 0 }}
                          tabIndex={-1}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, fontSize: "0.8rem" }}
                              noWrap
                            >
                              {[contact.first_name, contact.last_name]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </Typography>
                            {isOwner && (
                              <Chip
                                label="Propriétaire"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  bgcolor: alpha(
                                    theme.palette.warning.main,
                                    0.1
                                  ),
                                  color: theme.palette.warning.main,
                                }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Mail
                              size={10}
                              color={theme.palette.text.disabled}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: contact.email
                                  ? theme.palette.text.secondary
                                  : theme.palette.error.main,
                                fontSize: "0.7rem",
                              }}
                              noWrap
                            >
                              {contact.email || "Pas d'email"}
                            </Typography>
                          </Box>
                        </Box>
                        {contact.team && (
                          <Chip
                            label={contact.team}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: theme.palette.primary.main,
                            }}
                          />
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            {selectedContacts.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  color: theme.palette.text.secondary,
                  fontSize: "0.7rem",
                }}
              >
                <strong>{selectedContacts[0]?.email}</strong> recevra
                l'invitation en tant que propriétaire de l'organisation.
                {selectedContacts.length > 1 &&
                  ` ${selectedContacts.length - 1} autre(s) seront invité(s) comme utilisateur(s).`}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        {result ? (
          <Button
            onClick={onClose}
            variant="contained"
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
            }}
          >
            Fermer
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                color: theme.palette.text.secondary,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disableElevation
              disabled={selectedContactIds.size === 0 || loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: "8px",
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.primary.main, 0.6),
                  color: "white",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Création en cours...
                </Box>
              ) : (
                `Créer & Inviter (${selectedContactIds.size})`
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardOrgDialog;
